class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.fetchRecommendation = true;

    this.addEventListener('recommendationEvent', this.onRecommendationChange.bind(this));
  }

  onRecommendationChange(event) {
    localStorage.setItem('recommendationProduct', event.detail.recommendationProduct);
    localStorage.setItem('recommendationText', event.detail.text);
  }

  removeStoredRecommendation() {
    localStorage.removeItem('recommendationProduct');
    localStorage.removeItem('recommendationText');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    let cartRecommendations = document.querySelector('.cart-recommendations');
    let tempRecommendation = '';

    if (cartRecommendations) {
      const html = document.createElement('div');
      html.classList.add('cart-recommendations__wrapper');
      html.innerHTML = this.retrieveRecommendationText() || cartRecommendations.innerHTML;
      tempRecommendation = html.outerHTML;
    }

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if (triggeredBy && cartRecommendations) {
      cartRecommendations = document.querySelector('.cart-recommendations');
      this.fetchRecommendation = this.retrieveRecommendationProduct() === cartRecommendations.dataset.current ? false : true;

      if (this.fetchRecommendation) {
        await this.getRecommendations(cartRecommendations);
      } else {
        this.sethtml(cartRecommendations, '.cart-recommendations__wrapper', tempRecommendation);
      }
    }

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    if (!parsedState.sections) {
      parsedState.sections = await this.fallbackSections();
    }
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    let cartRecommendations = document.querySelector('.cart-recommendations');
    let tempRecommendation = '';

    if (cartRecommendations) {
      if (parsedState.item_count === 0) this.removeStoredRecommendation();
      const html = document.createElement('div');
      html.classList.add('cart-recommendations__wrapper');
      html.innerHTML = this.retrieveRecommendationText() || cartRecommendations.innerHTML;
      tempRecommendation = html.outerHTML;
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    cartRecommendations = document.querySelector('.cart-recommendations');

    if (cartRecommendations) {
      this.fetchRecommendation = this.retrieveRecommendationProduct() === cartRecommendations.dataset.current ? false : true;

      if (this.fetchRecommendation || parsedState.resetRecommendations) {
        await this.getRecommendations(cartRecommendations);
      } else {
        this.sethtml(cartRecommendations, '.cart-recommendations__wrapper', tempRecommendation);
      }
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  async getRecommendations(cartRecommendations) {
    const url = cartRecommendations.dataset.url;
    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        this.sethtml(cartRecommendations, '.cart-recommendations__wrapper', text);
        const recommendationEvent = new CustomEvent('recommendationEvent', {
          detail: {
            recommendationProduct: cartRecommendations.dataset.current,
            text
          }
        });
        this.dispatchEvent(recommendationEvent);
      });
  }

  sethtml(element, selector, text) {
    const html = document.createElement('div');
    html.innerHTML = text;
    const recommendations = html.querySelector(selector);

    if (recommendations && recommendations.innerHTML.trim().length) {
      element.innerHTML = recommendations.innerHTML;
    }
  }

  retrieveRecommendationText() {
    return localStorage.getItem('recommendationText');
  }

  retrieveRecommendationProduct() {
    return localStorage.getItem('recommendationProduct');
  }

  async fallbackSections() {
    const sections = await fetch(`${routes.cart_url}?sections=${this.getSectionsToRender().map((section) => section.id)}`);
    return await sections.json();
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
